<template>
  <div
    ref="referenceEl"
    data-testid="tooltip-trigger"
    v-bind="$attrs"
    @touchstart="isHovered = true"
    @mouseenter="isHovered = true"
    @touchend="isHovered = false"
    @mouseleave="isHovered = false"
  >
    <slot />
  </div>
  <Teleport to="body">
    <Transition name="fade">
      <div v-show="!disabled && isHovered" ref="floatingEl" class="tooltip" role="tooltip" :style="floatingStyles">{{ content }}</div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { ref, Ref, toRefs } from 'vue';
import { useFloating, autoUpdate, Placement, offset, flip, shift } from '@floating-ui/vue';

defineOptions({
  inheritAttrs: false,
});

const referenceEl: Ref<HTMLElement | null> = ref(null);
const floatingEl: Ref<HTMLElement | null> = ref(null);

const props = withDefaults(
  defineProps<{
    content: string;
    disabled?: boolean;
    hover?: boolean;
    placement?: Placement;
    offsetSize?: number;
  }>(),
  {
    disabled: false,
    hover: false,
    placement: 'top-start',
    offsetSize: 8,
  }
);
const { content, disabled, hover, placement, offsetSize } = toRefs(props);
const isHovered = ref(hover.value);
const { floatingStyles } = useFloating(referenceEl, floatingEl, {
  placement,
  middleware: [offset(offsetSize.value), flip(), shift()],
  whileElementsMounted: autoUpdate,
});
</script>

<style scoped>
.tooltip {
  @apply !bg-slate-600 !rounded-md !px-2 !py-1 !text-sm !text-white z-[9999];
  box-shadow: 0 6px 30px -6px rgba(0, 0, 0, 0.25);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
